import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import Loading from '@renderer/components/Loading'
import NotFound from '@renderer/pages/NotFound'
import ViewProject from '@renderer/pages/View'
import FailedToInitialize from '@renderer/pages/FailedToInitialize'
import { useConfigStore } from '@renderer/stores/configState'
import { useEnforceProjectLanguage } from '@renderer/hooks'
import {
  getStoredProjectEffectiveUpdatedAt,
  setStoredProjectEffectiveUpdatedAt
} from '@renderer/utils/projectVersionCache'
import { cacheHubDocument, readCachedProject } from '@renderer/utils/projectConfigCache'
import { hubToConfig, type HubDocument } from '@renderer/types/hubConfig'

/**
 * `/hub/:hubId/*` — the hub half of `ProjectRoute`.
 *
 * A hub lives in its own collection (`GET /api/hubs?id=`), so it is fetched,
 * version-checked and cached exactly like a project, then projected onto
 * `ConfigState` by `hubToConfig` so `View` renders it with no hub-specific
 * branches. Kiosk only — registered in `routes/index.tsx` before the
 * `/:projectId/*` catch-all, which would otherwise swallow `/hub/...` and try
 * to load a project called "hub".
 */
const HubRoute: React.FC = () => {
  const { hubId } = useParams()
  const [isLoading, setIsLoading] = useState(true)
  const [isValidHub, setIsValidHub] = useState<boolean | null>(null)
  const [isProjected, setIsProjected] = useState<boolean | null>(null)
  const { setConfig } = useConfigStore()
  // Same currency-driven language gating as a project: the hub Welcome and the
  // picker are customer-facing screens and must speak the members' language.
  useEnforceProjectLanguage()

  useEffect(() => {
    const validateAndLoadHub = async (): Promise<void> => {
      if (!hubId) {
        setIsValidHub(false)
        setIsLoading(false)
        return
      }

      const configSnapshot = useConfigStore.getState().config

      /**
       * Apply a hub document to the config store. Kept in one place so the
       * offline replay and the live load can never drift — a projection that
       * throws must land on FailedToInitialize, not on a half-set store.
       */
      const applyHub = (hub: HubDocument, effectiveUpdatedAt?: string): void => {
        setConfig(hubToConfig(hub))
        if (typeof hub.title === 'string' && hub.title.trim()) {
          document.title = `${hub.title.trim()} · Dreambooth`
        }
        if (effectiveUpdatedAt) setStoredProjectEffectiveUpdatedAt(hubId, effectiveUpdatedAt)
      }

      // Offline fallback, same contract as ProjectRoute: a booth whose Wi-Fi
      // dropped between guests re-runs this bootstrap on every hub → member →
      // hub hop, so an unreachable version endpoint would otherwise strand the
      // next guest on NotFound. Only a thrown fetch or a 5xx counts as
      // "unreachable" — a 4xx is a real answer about a real hub.
      const bootFromCache = (reason: unknown): boolean => {
        const cached = readCachedProject(hubId)
        if (!cached?.hub || cached.hub._id !== hubId) return false
        console.warn(`[HUB-ROUTE] offline_config_used for ${hubId}:`, reason)
        try {
          applyHub(cached.hub, cached.effectiveUpdatedAt)
          setIsValidHub(true)
          setIsProjected(true)
        } catch (error) {
          console.error('Error projecting cached hub:', error)
          setIsProjected(false)
        } finally {
          setIsLoading(false)
        }
        return true
      }

      try {
        let versionResponse: Response
        try {
          versionResponse = await fetch(
            `${process.env.VITE_API_URL}/api/hubs/version?id=${hubId}`,
            {
              credentials: 'include',
              cache: 'no-cache'
            }
          )
        } catch (networkError) {
          if (bootFromCache(networkError)) return
          throw networkError
        }

        if (!versionResponse.ok) {
          if (versionResponse.status >= 500 && bootFromCache(versionResponse.status)) return
          setIsValidHub(false)
          setIsLoading(false)
          return
        }

        const { effectiveUpdatedAt } = (await versionResponse.json()) as {
          effectiveUpdatedAt: string
        }

        const storedVersion = getStoredProjectEffectiveUpdatedAt(hubId)
        const configMatches = Boolean(
          configSnapshot && '_id' in configSnapshot && configSnapshot._id === hubId
        )
        if (storedVersion !== undefined && storedVersion === effectiveUpdatedAt && configMatches) {
          // Nothing changed and the hub is already the loaded config: leaving
          // the store untouched is what makes the member → hub hop instant.
          setIsValidHub(true)
          setIsProjected(true)
          setIsLoading(false)
          return
        }

        const cachedAtVersion = readCachedProject(hubId)
        const cacheHit =
          cachedAtVersion?.hub &&
          cachedAtVersion.hub._id === hubId &&
          cachedAtVersion.effectiveUpdatedAt === effectiveUpdatedAt
            ? cachedAtVersion.hub
            : null

        let hubData = cacheHit
        if (!hubData) {
          const hubResponse = await fetch(`${process.env.VITE_API_URL}/api/hubs?id=${hubId}`, {
            credentials: 'include',
            cache: 'no-cache'
          })
          if (!hubResponse.ok) {
            setIsValidHub(false)
            setIsLoading(false)
            return
          }
          hubData = (await hubResponse.json()) as HubDocument
        }

        if (!hubData || hubData._id !== hubId) {
          setIsValidHub(false)
          setIsLoading(false)
          return
        }
        setIsValidHub(true)

        try {
          applyHub(hubData, effectiveUpdatedAt)
          if (!cacheHit) cacheHubDocument(hubId, effectiveUpdatedAt, hubData)
          setIsProjected(true)
        } catch (error) {
          console.error('Error projecting hub config:', error)
          setIsProjected(false)
        }
        // No initializeSession here, deliberately. A hub owns no catalogue —
        // /api/filters|frames|stickers?projectId=<hubId> match nothing, since a
        // hub id belongs to no Project — and no session exists until the guest
        // picks a member, whose own ProjectRoute creates it.
      } catch (error) {
        console.error('Error validating hub:', error)
        setIsValidHub(false)
      } finally {
        setIsLoading(false)
      }
    }

    void validateAndLoadHub()
    // `config` is deliberately not a dep: a Welcome refresh writing the store
    // must not re-run the bootstrap.
  }, [hubId, setConfig])

  if (isLoading) return <Loading />
  if (!isValidHub) return <NotFound />
  if (isProjected === false) return <FailedToInitialize />

  return <ViewProject />
}

export default HubRoute
